
import { Vue, Options } from "vue-class-component";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import { RtcData } from "@/domain/entities/CargoV2";
import CargoV2FilterRtc from "@/app/ui/views/cargo-v2/components/cargo-v2-filter-rtc.vue";
import CargoV2CardRtc from "@/app/ui/views/cargo-v2/components/cargo-v2-card-rtc.vue";
import ModalBookingTruck from "@/app/ui/views/cargo-v2/components/modal-booking-truck.vue";
import PaginationV3 from "@/app/ui/components/pagination-v3/index.vue";
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { RequestRtcList } from "@/data/payload/api/CargoV2ApiRequest";
import { CheckEligible, useCargoV2 } from "../composables";
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import { cloneDeep } from "lodash";
import ButtonLoadMore from "../components/rtc-list/button-load-more.vue";

const { onCheckEligible } = useCargoV2();

@Options({
  components: {
    CargoV2FilterRtc,
    CargoV2CardRtc,
    PaginationV3,
    ModalBookingTruck,
    Skeleton,
    ButtonLoadMore
  }
})
class TabRtcTruck extends Vue {
  rtcListLimit = 5;
  get rtcListData() {
    return this.rtcList.data.slice(0, this.rtcListLimit);
  }
  filterForm = {
    destination: [] as string[],
    client: [] as string[],
    hasMinimumWeight: false,
    hasMinimumPieces: false,
    sort: ""
  };

  showModalForm = false;

  get isFirstLandingRtc() {
    return CargoV2Controller.isFirstLandingRtc;
  }

  async mounted() {
    await this.fetchReadyToCargoList();

    if (!this.rtcList.data.length && !this.hasActiveFilter && this.isFirstLandingRtc) {
      this.$router.push("ship");
    }
  }

  get rtcList() {
    return CargoV2Controller.rtcList;
  }

  get isLoading() {
    return CargoV2Controller.rtcLoading;
  }

  get hasActiveFilter(): boolean {
    return (
      this.filterForm.destination.length > 0 ||
      this.filterForm.client.length > 0
    );
  }

  get errorContainer() {
    return {
      errorIcon: "ill_spot_search",
      errorTitle: this.$t("cargoV2.create.rtcList.notFound.title"),
      errorMessage: this.$t("cargoV2.create.rtcList.notFound.message")
    };
  }

  async fetchReadyToCargoList() {
    try {
      MainAppController.showLoading();
      MainAppController.closeMessageModal();
      MainAppController.closeErrorMessage();

      const destinationCode = this.filterForm.destination.some(
        (item) => item === ""
      )
        ? ""
        : this.filterForm.destination.join(",");

      const sortOptions: {
        [key: string]: { sortBy: string; orderBy: string };
      } = {
        terdekat: { sortBy: "ct_time", orderBy: "ASC" },
        terlama: { sortBy: "ct_time", orderBy: "DESC" },
        default: { sortBy: "rtc_id", orderBy: "ASC" }
      };

      const selectedSortOption =
        sortOptions[this.filterForm.sort] || sortOptions.default;

      const sortBy = selectedSortOption.sortBy;
      const orderBy = selectedSortOption.orderBy;

      await CargoV2Controller.fetchReadyToCargoList(
        new RequestRtcList({
          page: this.rtcList.pagination.page,
          limit: this.rtcList.pagination.limit,
          destinationCode: destinationCode,
          transportType: "truck",
          sortBy: sortBy,
          orderBy: orderBy,
          clientParentIds: this.filterForm.client.length
            ? this.filterForm.client.map((item) => Number(item))
            : [],
          hubId: getHubData()?.hubId || 0,
        })
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  onFilter(form: any) {
    this.filterForm = form;
    this.rtcList.pagination.page = 1;
    this.fetchReadyToCargoList();
  }

  onChangePagination() {
    this.fetchReadyToCargoList();
  }

  get form() {
    return CargoV2Controller.form;
  }

  onReset() {
    this.showModalForm = false;
  }

  async onProcess(data: RtcData) {
    this.onBeforeProcess(data, "process", () => (this.showModalForm = true));
  }

  async onEdit(data: RtcData) {
    this.onBeforeProcess(data, "rtc-edit", () =>
      this.$router.push(`/cargo-v2/create/rtc/${data.id}`)
    );
  }

  async onBeforeProcess(data: RtcData, flow: string, success: () => void) {
    try {
      await CargoV2Controller.getDetailReadyToCargo({
        id: data.id,
        flow
      });

      await CargoV2Controller.mapRtcDetailToCargoForm();

      const cargoProductList = await CargoV2Controller.getCargoProductList(
        "active"
      );
      CargoV2Controller.setCargoProductList(cargoProductList);
      this.handleProductInactive();

      await onCheckEligible(
        new CheckEligible({
          success,
          close: () => {
            if (!this.form.bagOrStt.length) {
              this.fetchReadyToCargoList();
              return;
            }
            this.form.actualTotalCargoVolumeweight = this.form.totalAccumulationEstimationVolumeWeightDataTable;
            this.form.cargoActualPiece = this.form.totalAccumulationActualPieceDataTable;
            this.form.actualTotalCargoGrossweigth = this.form.totalAccumulationEstimationGrossWeightDataTable;
            CargoV2Controller.setForm(this.form);
            this.$router.push(`/cargo-v2/create/rtc/${data.id}`)
          }
        })
      );
    } catch (error) {
      this.fetchReadyToCargoList();
    }
  }

  handleProductInactive() {
    const productFound = CargoV2Controller.cargoProductList.find(
      (product) => product.cargoProductTypeCode === this.form.productCargo
    );

    if (!productFound)
      this.form.productCargo =
        CargoV2Controller.cargoProductList[0].cargoProductTypeCode;
  }

  onCreateResponse(resp: any) {
    if (resp.success) {
      const destination = cloneDeep(this.form.destinationCityCode).code;
      const message = this.$t("cargoV2.create.snackbar.booked", {
        cargoNo: resp.cargoNo,
        destination
      });
      MainAppController.setSnackbarMessage(message);
      MainAppController.setSnackbarPositivity(true);
      MainAppController.setSnackbarTimeout(3000);
      MainAppController.setSnackbar(true);
      this.fetchReadyToCargoList();
    } else {
      if (resp.message) {
        MainAppController.showErrorMessage(resp.message);
      }
    }
  }

  onErrorCreateCargo(err: any, onRetry: () => void) {
    MainAppController.showErrorMessage(
      parsingErrorResponse(err, this.$t("cargoV2.create.error.bookingFailedTitle"), () => {
        MainAppController.closeErrorMessage();
        onRetry();
      })
    );
  }

  onCloseCheckEligible() {
    if (!this.form.bagOrStt.length) {
      CargoV2Controller.resetForm();
      this.fetchReadyToCargoList();
      return;
    }
    this.form.actualTotalCargoVolumeweight = this.form.totalAccumulationEstimationVolumeWeightDataTable;
    this.form.cargoActualPiece = this.form.totalAccumulationActualPieceDataTable;
    this.form.actualTotalCargoGrossweigth = this.form.totalAccumulationEstimationGrossWeightDataTable;
    CargoV2Controller.setForm(this.form);
    this.$router.push(`/cargo-v2/create/rtc/${this.form.rtcId}`)
  }

  onRefresh() {
    CargoV2Controller.resetForm();
    this.fetchReadyToCargoList();
  }
}

export default TabRtcTruck;
